import { TIKTOK_URL_REGEX, YT_SHORTS_URL_REGEX, YT_WATCH_URL_REGEX } from 'src/constants/sentinel';
import { ParsedVideo } from 'src/types/sentinel';

export const VIDEO_URL_PATTERN =
	'https?://(www\\.)?(tiktok\\.com|vm\\.tiktok\\.com).*|https?://(www\\.)?youtube\\.com/shorts/.*|https?://(www\\.)?youtube\\.com/watch\\?v=.*';

// Rewrites YouTube URLs to the canonical https://youtube.com/watch?v=<id> form expected by the
// processing pipeline: drops non-essential query params (e.g. list/index/start) and converts
// /shorts/<id> into the watch form. Non-YouTube (and unparseable) URLs are returned untouched.
export function canonicalizeVideoUrl(url: string): string {
	if (!url || typeof url !== 'string') {
		return url;
	}

	if (YT_WATCH_URL_REGEX.test(url)) {
		try {
			const videoId = new URL(url).searchParams.get('v');
			return videoId ? `https://youtube.com/watch?v=${videoId}` : url;
		} catch {
			return url;
		}
	}

	if (YT_SHORTS_URL_REGEX.test(url)) {
		try {
			const videoId = new URL(url).pathname.split('/').filter(Boolean).pop();
			return videoId ? `https://youtube.com/watch?v=${videoId}` : url;
		} catch {
			return url;
		}
	}

	return url;
}

export function isValidVideoUrl(url: string): boolean {
	if (!url || typeof url !== 'string') {
		return false;
	}

	return TIKTOK_URL_REGEX.test(url) || YT_SHORTS_URL_REGEX.test(url) || YT_WATCH_URL_REGEX.test(url);
}

export function parseVideoUrl(url: string): ParsedVideo {
	const invalidResult: ParsedVideo = {
		isValid: false,
		platform: null,
		videoId: null,
		embedUrl: null,
		orientation: 'horizontal'
	};

	if (!isValidVideoUrl(url)) {
		return invalidResult;
	}
	if (YT_WATCH_URL_REGEX.test(url)) {
		try {
			const urlObj = new URL(url);
			const videoId = urlObj.searchParams.get('v');

			if (!videoId) {
				return invalidResult;
			}

			return {
				isValid: true,
				platform: 'youtube',
				videoId,
				embedUrl: `https://www.youtube.com/embed/${videoId}`,
				orientation: 'horizontal'
			};
		} catch {
			return invalidResult;
		}
	}

	if (YT_SHORTS_URL_REGEX.test(url)) {
		try {
			const urlObj = new URL(url);
			const pathParts = urlObj.pathname.split('/');
			const videoId = pathParts[pathParts.length - 1];

			if (!videoId) {
				return invalidResult;
			}

			return {
				isValid: true,
				platform: 'youtube-short',
				videoId,
				embedUrl: `https://www.youtube.com/embed/${videoId}`,
				orientation: 'vertical'
			};
		} catch {
			return invalidResult;
		}
	}

	if (TIKTOK_URL_REGEX.test(url)) {
		try {
			const urlObj = new URL(url);
			const pathParts = urlObj.pathname.split('/').filter((part) => part);
			const videoId = pathParts[pathParts.length - 1];

			if (!videoId) {
				return invalidResult;
			}

			return {
				isValid: true,
				platform: 'tiktok',
				videoId,
				embedUrl: `https://www.tiktok.com/player/v1/${videoId}`,
				orientation: 'vertical'
			};
		} catch {
			return invalidResult;
		}
	}

	return invalidResult;
}
